import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { ChevronDown, LayoutList, Plus, RotateCcw, Settings, Workflow } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { ChildTicketTask } from '@/lib/services/childTicketService'
import { TicketService } from '@/lib/services/ticket'
import type { TicketPart } from '@/types/parentTicket'
import ApplyTaskTemplateDrawer from './ApplyTaskTemplateDrawer'
import CreateTaskFromTemplateDrawer from './CreateTaskFromTemplateDrawer'
import TaskDetailDrawer from './TaskDetailDrawer'
import TaskListView from './TaskListView'
import TaskPipelineView from './TaskPipelineView'
import TasksOperationalBanner from './TasksOperationalBanner'
import ResetWorkflowModal, { type ResetWorkflowPreview } from './ResetWorkflowModal'
import { childTicketService } from '@/lib/services/childTicketService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { resolveTaskUiType } from './taskTypeBadge'
import {
  defaultPipelineView,
  filterTasks,
  type TaskFilter,
} from './taskUtils'

type ViewMode = 'list' | 'pipeline'

const FILTERS: { id: TaskFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'blocked', label: 'Blocked' },
  { id: 'breached', label: 'Breached' },
  { id: 'completed', label: 'Completed' },
]

type Props = {
  parentTicketId: string
  tasks: ChildTicketTask[]
  loading?: boolean
  onRefresh: () => void | Promise<void>
  ticketPropertiesProps?: Record<string, unknown>
  onParentRefresh?: () => void | Promise<void>
  storeId?: number | null
  assetsCount?: number
  /** When set, opens the task drawer for this child ticket id */
  openTaskId?: number | null
  onOpenTaskHandled?: () => void
  /** When set, opens create drawer with defaults (from parent ticket field badges) */
  pendingCreateDefaults?: Partial<ChildTicketTask> | null
  onPendingCreateHandled?: () => void
}

export default function TaskListPanel({
  parentTicketId,
  tasks,
  loading,
  onRefresh,
  ticketPropertiesProps,
  onParentRefresh,
  storeId,
  assetsCount = 0,
  openTaskId,
  onOpenTaskHandled,
  pendingCreateDefaults,
  onPendingCreateHandled,
}: Props) {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [filter, setFilter] = useState<TaskFilter>('all')
  const [agingBucketFilter, setAgingBucketFilter] = useState<
    '' | 'fresh' | 'aging' | 'old' | 'critical'
  >('')
  const [viewInitialized, setViewInitialized] = useState(false)
  const [editingTask, setEditingTask] = useState<ChildTicketTask | null>(null)
  const [createDefaults, setCreateDefaults] = useState<Partial<ChildTicketTask> | null>(null)
  const [resetting, setResetting] = useState(false)
  const [resetPreview, setResetPreview] = useState<ResetWorkflowPreview | null>(null)
  const [loadingResetPreview, setLoadingResetPreview] = useState(false)
  const { isOpen: drawerOpen, onOpen: openDrawer, onClose: closeDrawer } = useDisclosure()
  const { isOpen: createOpen, onOpen: openCreateDrawer, onClose: closeCreateDrawer } = useDisclosure()
  const { isOpen: templateOpen, onOpen: openTemplate, onClose: closeTemplate } = useDisclosure()
  const { isOpen: resetModalOpen, onOpen: openResetModal, onClose: closeResetModal } = useDisclosure()

  const hasPartsProcurementTask = useMemo(
    () => tasks.some((task) => resolveTaskUiType(task) === 'parts_procurement'),
    [tasks],
  )

  const ticketPartsFromProps = useMemo(() => {
    const ticket = ticketPropertiesProps?.ticket as { ticketParts?: TicketPart[] } | undefined
    return Array.isArray(ticket?.ticketParts) ? ticket.ticketParts : []
  }, [ticketPropertiesProps])

  const [ticketParts, setTicketParts] = useState<TicketPart[]>([])

  useEffect(() => {
    if (!hasPartsProcurementTask) {
      setTicketParts([])
      return
    }

    if (ticketPartsFromProps.length > 0) {
      setTicketParts(ticketPartsFromProps)
      return
    }

    let cancelled = false
    void TicketService.getTicketParts(parentTicketId).then((res) => {
      if (cancelled) return
      if (res.success && res.data) {
        setTicketParts(res.data)
      }
    })

    return () => {
      cancelled = true
    }
  }, [hasPartsProcurementTask, ticketPartsFromProps, parentTicketId])

  useEffect(() => {
    if (templateOpen) {
      void onRefresh()
    }
  }, [templateOpen, onRefresh])

  useEffect(() => {
    if (viewInitialized || tasks.length === 0) return
    setViewMode(defaultPipelineView(tasks) ? 'pipeline' : 'list')
    setViewInitialized(true)
  }, [tasks, viewInitialized])

  const filteredTasks = useMemo(() => {
    const base = filterTasks(tasks, filter)
    if (!agingBucketFilter) return base
    return base.filter((t) => {
      // Backend already computes these; if missing, don't include in bucketed view.
      const cat = t.age_in_status_category ?? null
      const aging = (t as any)?.aging_category ?? null
      return cat === agingBucketFilter || aging === agingBucketFilter
    })
  }, [tasks, filter, agingBucketFilter])

  const handleRefreshAll = useCallback(async () => {
    await onRefresh()
    await onParentRefresh?.()
    if (hasPartsProcurementTask && ticketPartsFromProps.length === 0) {
      const res = await TicketService.getTicketParts(parentTicketId)
      if (res.success && res.data) {
        setTicketParts(res.data)
      }
    }
  }, [onRefresh, onParentRefresh, hasPartsProcurementTask, ticketPartsFromProps.length, parentTicketId])

  const handleTemplateApplied = useCallback(async () => {
    setViewMode('pipeline')
    await handleRefreshAll()
  }, [handleRefreshAll])

  const openCreate = () => {
    setEditingTask(null)
    setCreateDefaults(null)
    openCreateDrawer()
  }

  const openEdit = (task: ChildTicketTask) => {
    setCreateDefaults(null)
    setEditingTask(task)
    openDrawer()
  }

  useEffect(() => {
    if (openTaskId == null || loading) return
    const task = tasks.find((t) => t.id === openTaskId)
    if (!task) return
    setCreateDefaults(null)
    setEditingTask(task)
    openDrawer()
    onOpenTaskHandled?.()
  }, [openTaskId, tasks, loading, openDrawer, onOpenTaskHandled])

  useEffect(() => {
    if (!pendingCreateDefaults || loading) return
    setEditingTask(null)
    setCreateDefaults(pendingCreateDefaults)
    openDrawer()
    onPendingCreateHandled?.()
  }, [pendingCreateDefaults, loading, openDrawer, onPendingCreateHandled])

  const openResetWorkflow = async () => {
    openResetModal()
    setLoadingResetPreview(true)
    setResetPreview(null)
    try {
      const preview = await childTicketService.previewClearAll(parentTicketId)
      setResetPreview(preview)
    } catch (err) {
      showError(err as Error)
      closeResetModal()
    } finally {
      setLoadingResetPreview(false)
    }
  }

  const handleResetWorkflow = async (includeActive: boolean) => {
    setResetting(true)
    try {
      const result = await childTicketService.clearAll(parentTicketId, { includeActive })
      closeResetModal()
      setResetPreview(null)

      if (result.removed <= 0) {
        showSuccess('No tasks to remove')
      } else if (result.skipped_active > 0) {
        showSuccess(
          `Removed ${result.removed} task${result.removed === 1 ? '' : 's'}; kept ${result.skipped_active} active task${result.skipped_active === 1 ? '' : 's'}`,
        )
      } else {
        showSuccess(`Removed ${result.removed} task${result.removed === 1 ? '' : 's'}`)
      }

      setViewInitialized(false)
      await handleRefreshAll()
    } catch (err) {
      showError(err as Error)
    } finally {
      setResetting(false)
    }
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" gap={3} flexWrap="wrap" mb={4}>
        <HStack spacing={2} flexWrap="wrap">
          <Button size="sm" leftIcon={<Plus size={14} />} colorScheme="blue" onClick={openCreate}>
            Create task
          </Button>
          <Button size="sm" leftIcon={<Workflow size={14} />} variant="outline" onClick={openTemplate}>
            Apply template
          </Button>
          {tasks.length > 0 ? (
            <Button
              size="sm"
              leftIcon={<RotateCcw size={14} />}
              variant="outline"
              colorScheme="red"
              onClick={() => void openResetWorkflow()}
              isLoading={resetting || loadingResetPreview}
            >
              Reset workflow
            </Button>
          ) : null}
          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              variant="ghost"
              leftIcon={<Settings size={14} />}
              rightIcon={<ChevronDown size={14} />}
            >
              Templates
            </MenuButton>
            <MenuList minW="180px">
              <MenuItem fontSize="sm" onClick={() => navigate('/service-catalog/task-templates')}>
                Manage templates
              </MenuItem>
              <MenuItem fontSize="sm" onClick={() => navigate('/service-catalog/task-templates/create')}>
                Create template
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
        <ButtonGroup size="sm" isAttached variant="outline">
          <Button
            leftIcon={<LayoutList size={14} />}
            colorScheme={viewMode === 'list' ? 'blue' : undefined}
            variant={viewMode === 'list' ? 'solid' : 'outline'}
            onClick={() => setViewMode('list')}
          >
            List
          </Button>
          <Button
            leftIcon={<Workflow size={14} />}
            colorScheme={viewMode === 'pipeline' ? 'blue' : undefined}
            variant={viewMode === 'pipeline' ? 'solid' : 'outline'}
            onClick={() => setViewMode('pipeline')}
          >
            Pipeline
          </Button>
        </ButtonGroup>
      </Flex>

      <HStack spacing={2} flexWrap="wrap" mb={4}>
        {FILTERS.map((item) => (
          <Button
            key={item.id}
            size="xs"
            variant={filter === item.id ? 'solid' : 'outline'}
            colorScheme={filter === item.id ? 'blue' : 'gray'}
            borderRadius="full"
            onClick={() => setFilter(item.id)}
          >
            {item.label}
          </Button>
        ))}
        <Select
          size="xs"
          maxW="180px"
          value={agingBucketFilter}
          onChange={(e) => setAgingBucketFilter(e.target.value as any)}
        >
          <option value="">Age in status: All</option>
          <option value="fresh">Fresh</option>
          <option value="aging">Aging</option>
          <option value="old">Old</option>
          <option value="critical">Critical</option>
        </Select>
      </HStack>

      {!loading && tasks.length > 0 ? (
        <TasksOperationalBanner
          tasks={tasks}
          storeId={storeId}
          assetsCount={assetsCount}
          onOpenTask={openEdit}
        />
      ) : null}

      {loading ? (
        <HStack py={10} justify="center">
          <Spinner size="md" color={colors.primary} />
          <Text color={colors.textSecondary}>Loading tasks…</Text>
        </HStack>
      ) : tasks.length === 0 ? (
        <Box py={10} textAlign="center" maxW="md" mx="auto">
          <Text color={colors.textSecondary} mb={4} fontSize="sm">
            No tasks yet. Choose a workflow template to create task steps for this ticket.
          </Text>
          <HStack justify="center" spacing={2} flexWrap="wrap">
            <Button size="sm" colorScheme="blue" leftIcon={<Workflow size={14} />} onClick={openTemplate}>
              Apply template
            </Button>
            <Button size="sm" variant="outline" onClick={openCreate}>
              Create task
            </Button>
          </HStack>
        </Box>
      ) : filteredTasks.length === 0 ? (
        <Text py={8} textAlign="center" color={colors.textSecondary}>
          No tasks match this filter.
        </Text>
      ) : viewMode === 'pipeline' ? (
        <TaskPipelineView tasks={filteredTasks} onTaskClick={openEdit} />
      ) : (
        <TaskListView
          parentTicketId={parentTicketId}
          tasks={filteredTasks}
          ticketParts={ticketParts}
          onTasksChange={handleRefreshAll}
          ticketPropertiesProps={ticketPropertiesProps}
          onEditTask={openEdit}
          onCreateTask={openCreate}
        />
      )}

      <TaskDetailDrawer
        isOpen={drawerOpen}
        onClose={closeDrawer}
        parentTicketId={parentTicketId}
        task={editingTask}
        siblingTasks={tasks}
        onOpenTask={openEdit}
        createDefaults={createDefaults}
        onSaved={() => void handleRefreshAll()}
        ticketPropertiesProps={ticketPropertiesProps}
      />

      <CreateTaskFromTemplateDrawer
        isOpen={createOpen}
        onClose={closeCreateDrawer}
        parentTicketId={parentTicketId}
        onCreated={handleRefreshAll}
      />

      <ApplyTaskTemplateDrawer
        isOpen={templateOpen}
        onClose={closeTemplate}
        parentTicketId={parentTicketId}
        existingTaskCount={tasks.length}
        onApplied={handleTemplateApplied}
      />

      <ResetWorkflowModal
        isOpen={resetModalOpen}
        onClose={() => {
          if (resetting) return
          closeResetModal()
          setResetPreview(null)
        }}
        preview={resetPreview}
        loadingPreview={loadingResetPreview}
        submitting={resetting}
        onRemoveSafeOnly={() => void handleResetWorkflow(false)}
        onRemoveAll={() => void handleResetWorkflow(true)}
      />
    </Box>
  )
}
